import React, {
  Fragment, useRef, useEffect, useState, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';

import InfoIcon from '../../../../assets/info-icon.svg';
import CloseIcon from '../../../../assets/cross-icon-new.svg';
import FiltersIcon from '../../../../assets/filters-icon.svg';
import useResponsive from '../../../hooks/useResponsive';
import InputComponent from '../InputComponent';
import CheckboxComponent from '../CheckboxComponent';
import Button from '../Button';
import MultipleDropdown from '../MultipleDropdown';
import Dropdown from '../DropdownV2';
import DatePickerInput from './DatePicker';

import styles from '../../../styles/views/components/Filter/Filter.module.scss';

export default function Filter({
  filtersFixtures,
  state,
  handlers,
  isFilterOpen,
  setIsFilterOpen,
  getSelectedDatePickers,
}) {
  const selectedDates = useMemo(() => getSelectedDatePickers(), [getSelectedDatePickers]);
  const { isMobile, isTablet } = useResponsive();
  const [datePickers, setDate] = useState({
    createFromDate: '',
    createToDate: '',
    updateFromDate: '',
    updateToDate: '',
    lastLoggedInFromDate: '',
    lastLoggedInToDate: '',
  });

  const {
    handleInputChange,
    createAllGroupName,
    toggleFilterGroup,
    toggleFilter,
    handleChangeMultipleDropdown,
    clearAllFilters,
    checkAnyFiltersSelected,
    handleDatePicker,
  } = handlers;

  const refsDropdown = [];
  const refsInput = [];
  const filterRef = useRef(null);

  useEffect(() => {
    const updatedDatePickers = {};
    updatedDatePickers.createFromDate = selectedDates.createFromDate ? new Date(selectedDates.createFromDate) : '';
    updatedDatePickers.createToDate = selectedDates.createToDate ? new Date(selectedDates.createToDate) : '';
    updatedDatePickers.updateFromDate = selectedDates.updateFromDate ? new Date(selectedDates.updateFromDate) : '';
    updatedDatePickers.updateToDate = selectedDates.updateToDate ? new Date(selectedDates.updateToDate) : '';
    updatedDatePickers.lastLoggedInFromDate = selectedDates.lastLoggedInFromDate ? new Date(selectedDates.lastLoggedInFromDate) : '';
    updatedDatePickers.lastLoggedInToDate = selectedDates.lastLoggedInToDate ? new Date(selectedDates.lastLoggedInToDate) : '';
    setDate(updatedDatePickers);
  }, [selectedDates]);

  useEffect(() => {
    const checkClickOutside = (e) => {
      if (isFilterOpen && filterRef.current && !filterRef.current.contains(e.target)) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', checkClickOutside);
    return () => document.removeEventListener('mousedown', checkClickOutside);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterOpen]);

  const clearFilters = () => {
    refsDropdown.forEach((r) => r?.clearValue());
    refsInput.forEach((r) => (r.value = ''));
    clearAllFilters();
  };

  return (
    <>
      <div className={[styles.container, isFilterOpen ? styles.containerOpen : ''].join(' ')} ref={filterRef}>
        <div className={styles.headerWrapper}>
          {checkAnyFiltersSelected() ? (
            <Button
              extraStyles={{ padding: '8px 24px', height: isMobile ? '32px' : 'initial' }}
              onClick={clearFilters}
              color="gold"
              value={t('button.clearAllFilters', { ns: i18next.Namespace.COMMON })}
              variant="secondary"
            />
          ) : (
            <div className={styles.titleWrapper}>
              <FiltersIcon />
              <span className={styles.title}>{t('button.filters', { ns: i18next.Namespace.COMMON })}</span>
            </div>
          )}
          <CloseIcon className={styles.closeIcon} onClick={() => setIsFilterOpen(false)} />
        </div>
        <hr className={styles.hrLine} />
        {filtersFixtures.map((filterFixture) => {
          const meta = filterFixture;

          switch (meta.type) {
            case 'input':
              return (
                <Fragment key={meta.id}>
                  {filterFixture.options.map((filter) => (
                    <Fragment key={filter.name}>
                      {!meta.groupLabel
                        ? (
                          <InputComponent
                            refs={refsInput}
                            name={filter.name}
                            onChange={(e) => {
                              handleInputChange(filter, e.target.value);
                            }}
                            label={filter.label}
                            placeholder={filter.placeholder}
                            defaultValue={state[filter.name]?.inputValue}
                            maxLength={50}
                          />
                        )
                        : (
                          <div className={styles.filterContainer}>
                            {filterFixture.subOption && <br />}
                            <div className={[styles.title, styles.inputTitle].join(' ')}>{meta.groupLabel}</div>
                            <div className={styles.filter}>
                              <InputComponent
                                refs={refsInput}
                                name={filter.name}
                                onChange={(e) => {
                                  handleInputChange(filter, e.target.value);
                                }}
                                label={filter.label}
                                extraStyles={{ width: '100%' }}
                                placeholder={filter.placeholder}
                                defaultValue={state[filter.name]?.inputValue}
                                maxLength={50}
                              />
                              {filterFixture.subOption && (
                                <>
                                  <Dropdown
                                    refs={refsDropdown}
                                    key={filterFixture.subOption.name}
                                    name={filterFixture.subOption.name}
                                    label={filterFixture.subOption.label}
                                    placeholder={filterFixture.subOption.placeholder}
                                    isSearchable
                                    optionValue="value"
                                    optionLabel="label"
                                    options={filterFixture.subOption.options}
                                    primitiveValue={state.country?.value}
                                    onChange={(selectedOption) => {
                                      handleChangeMultipleDropdown(filterFixture.subOption.name, selectedOption);
                                    }}
                                    isClearable
                                  />
                                  <hr />
                                </>
                              )}
                            </div>
                          </div>
                        )}
                    </Fragment>
                  ))}
                </Fragment>
              );
            case 'checkbox':
              return (
                <div key={meta.id} className={styles.filterContainer}>
                  <div className={styles.title}>{meta.groupLabel}</div>
                  <div className={styles.filter}>
                    {meta.allCheckboxLabel && (
                    <CheckboxComponent
                      size={isMobile ? '12px' : '16px'}
                      checked={!!state[createAllGroupName(meta.group)]?.checked}
                      onChange={() => toggleFilterGroup(meta.group)}
                      name={meta.allCheckboxLabel}
                      labelStyling={(isMobile || isTablet) ? { paddingLeft: '6px', lineHeight: '16px', fontSize: '12px' } : { paddingLeft: '6px' }}
                    >
                      {meta.allCheckboxLabel}
                    </CheckboxComponent>
                    )}

                    {filterFixture.options.map((filter) => (
                      <CheckboxComponent
                        key={filter.name}
                        size={isMobile ? '12px' : '16px'}
                        checked={!!state[filter.name]?.checked}
                        onChange={() => toggleFilter(filter)}
                        name={filter.name}
                        stylesConfig={{ wrapper: filter.wrapperStyle }}
                        labelStyling={(isMobile || isTablet) ? { paddingLeft: '6px', lineHeight: '16px', fontSize: '12px' } : { paddingLeft: '6px' }}
                      >
                        {meta.count && <span className={styles.count}>{filter.index}</span>} <span style={{ cursor: 'pointer' }} data-tip={filter.tooltip || ''}>{filter.label}</span>
                      </CheckboxComponent>
                    ))}
                  </div>
                </div>
              );
            case 'dropdown/multiple':
              return (
                <MultipleDropdown
                  refs={refsDropdown}
                  key={filterFixture.id}
                  name={filterFixture.name}
                  label={filterFixture.label}
                  placeholder={filterFixture.placeholder}
                  options={filterFixture.options}
                  defaultValue={state[filterFixture.name]}
                  onChange={(selectedOptions) => handleChangeMultipleDropdown(filterFixture.name, selectedOptions)}
                />
              );
            case 'datePicker':
              return (
                <div key={meta.id} className={styles.filterContainer}>
                  <div className={styles.titleDate}>
                    {meta.groupLabel}
                    <InfoIcon data-tip={meta.tooltip || t('tooltips.datePickerFilter', { ns: i18next.Namespace.COMMON })} />
                  </div>
                  <div className={styles.filter} style={{ flexDirection: 'row', gap: '0' }}>
                    <DatePicker
                      customInput={(
                        <DatePickerInput name={meta.options[0].name} inputLabel={meta.options[0].inputLabel} />
                      )}
                      selected={datePickers[meta.options[0].name]}
                      onChange={(date) => {
                        handleDatePicker(filterFixture.options[0], date);
                        setDate((dates) => ({ ...dates, [meta.options[0].name]: date }));
                      }}
                      locale="en"
                      minDate={meta.options[0].minDate || null}
                    />
                    <DatePicker
                      customInput={(
                        <DatePickerInput name={meta.options[1].name} inputLabel={meta.options[1].inputLabel} />
                      )}
                      selected={datePickers[meta.options[1].name]}
                      onChange={(date) => {
                        handleDatePicker(filterFixture.options[1], date);
                        setDate((dates) => ({ ...dates, [meta.options[1].name]: date }));
                      }}
                      locale="en"
                      minDate={meta.options[1].minDate || null}
                    />
                  </div>
                </div>
              );
            default:
              return null;
          }
        })}
      </div>
    </>
  );
}

Filter.propTypes = {
  filtersFixtures: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  state: PropTypes.shape({
    country: PropTypes.shape({
      value: PropTypes.string,
    }),
  }).isRequired,
  handlers: PropTypes.shape({
    handleInputChange: PropTypes.func.isRequired,
    toggleFilter: PropTypes.func.isRequired,
    toggleFilterGroup: PropTypes.func.isRequired,
    createAllGroupName: PropTypes.func.isRequired,
    handleChangeMultipleDropdown: PropTypes.func.isRequired,
    clearAllFilters: PropTypes.func.isRequired,
    checkAnyFiltersSelected: PropTypes.func.isRequired,
    handleDatePicker: PropTypes.func.isRequired,
  }).isRequired,
  isFilterOpen: PropTypes.bool.isRequired,
  setIsFilterOpen: PropTypes.func.isRequired,
  getSelectedDatePickers: PropTypes.func.isRequired,
};
